package com.eladfinish.bible.contest.screens.search.suggestions

import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.data.common.FileStorage
import com.eladfinish.bible.contest.popular_searches_seed_json
import com.eladfinish.contract.suggestions.SearchSuggestionsSnapshot
import com.russhwolf.settings.Settings
import io.github.aakira.napier.Napier
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json

/**
 * The popular searches of all users, as the app sees them: a snapshot downloaded from the server
 * once a day and kept on disk, with the copy bundled in the app as the fallback for the first run
 * and for offline use.
 */
interface PopularSearchesRepository {

    /** The best snapshot available right now. Never waits for the network. */
    suspend fun snapshot(): SearchSuggestionsSnapshot

    /**
     * Downloads a newer snapshot when the stored one is a day old. Cheap when it is not, so call
     * it whenever the search screen opens.
     */
    suspend fun refreshIfStale()
}

class CachedPopularSearchesRepository(
    private val api: PopularSearchesApi,
    private val configProvider: SearchSuggestionsConfigProvider,
    private val fileStorage: FileStorage,
    private val settings: Settings,
    private val assetReader: AssetReader,
    private val json: Json,
    private val clock: () -> Long,
) : PopularSearchesRepository {

    private val mutex = Mutex()
    private var cached: SearchSuggestionsSnapshot? = null

    override suspend fun snapshot(): SearchSuggestionsSnapshot = mutex.withLock {
        cached ?: chooseSnapshot().also { cached = it }
    }

    /**
     * The downloaded snapshot wins, unless it has no entries: the server's table is young and
     * its list may be empty for weeks, and an empty list must not replace a useful seed
     * (`prefersSeedOverEmptySnapshot` in the plan).
     */
    private fun chooseSnapshot(): SearchSuggestionsSnapshot {
        val stored = readStored()
        if (stored != null && stored.entries.isNotEmpty()) return stored

        val seed = readSeed()
        return if (seed.entries.isNotEmpty()) seed else stored ?: seed
    }

    override suspend fun refreshIfStale() {
        val now = clock()
        val lastFetch = settings.getLong(Constants.PREF_SEARCH_SUGGESTIONS_LAST_FETCH_EPOCH_MS, 0L)
        val lastAttempt = settings.getLong(Constants.PREF_SEARCH_SUGGESTIONS_LAST_ATTEMPT_EPOCH_MS, 0L)
        if (now - lastFetch < REFRESH_INTERVAL_MS || now - lastAttempt < RETRY_INTERVAL_MS) return

        settings.putLong(Constants.PREF_SEARCH_SUGGESTIONS_LAST_ATTEMPT_EPOCH_MS, now)

        api.fetch(configProvider.config().snapshotUrl)
            .onSuccess { snapshot ->
                if (snapshot.version != SearchSuggestionsSnapshot.FORMAT_VERSION) {
                    Napier.w(tag = TAG, message = "Ignoring snapshot of unknown version ${snapshot.version}")
                    return
                }

                mutex.withLock {
                    fileStorage.saveFile(FOLDER, FILE_NAME, json.encodeToString(SearchSuggestionsSnapshot.serializer(), snapshot))
                    settings.putLong(Constants.PREF_SEARCH_SUGGESTIONS_LAST_FETCH_EPOCH_MS, now)
                    cached = chooseSnapshot()
                }
            }
            .onFailure { throwable ->
                Napier.w(tag = TAG, throwable = throwable, message = "Could not refresh the popular searches")
            }
    }

    private fun readStored(): SearchSuggestionsSnapshot? {
        val content = fileStorage.readFile(FOLDER, FILE_NAME) ?: return null

        return parseOrNull(content, origin = "stored file")
    }

    private fun readSeed(): SearchSuggestionsSnapshot =
        parseOrNull(assetReader.readFile(SharedRes.assets.search.popular_searches_seed_json), origin = "seed asset")
            ?: EMPTY

    private fun parseOrNull(content: String, origin: String): SearchSuggestionsSnapshot? =
        try {
            json.decodeFromString(SearchSuggestionsSnapshot.serializer(), content)
                .takeIf { it.version == SearchSuggestionsSnapshot.FORMAT_VERSION }
        } catch (e: SerializationException) {
            Napier.e(tag = TAG, throwable = e, message = "Unreadable popular searches $origin")
            null
        } catch (e: IllegalArgumentException) {
            Napier.e(tag = TAG, throwable = e, message = "Unreadable popular searches $origin")
            null
        }

    companion object {
        const val FOLDER = "search_suggestions"
        const val FILE_NAME = "popular_searches"
        const val REFRESH_INTERVAL_MS = 24L * 60 * 60 * 1000
        const val RETRY_INTERVAL_MS = 60L * 60 * 1000
        private const val TAG = "PopularSearches"

        val EMPTY = SearchSuggestionsSnapshot(
            generatedAt = "",
            threshold = 0,
            recentDays = 7,
            entries = emptyList(),
        )
    }
}
